/**
 * MagnetHeader — the ONE piece of EE chrome sanctioned on a full-document
 * magnet landing/thank-you page (see MagnetPage.tsx + sop-ee-lead-magnets.md).
 *
 * Full-doc magnet bodies are CC's own complete HTML documents (own <style>,
 * own reset, own fonts) injected via dangerouslySetInnerHTML — this header
 * renders as a SIBLING above that injected div, so it must use inline styles
 * exclusively. CC's injected `body{}`/`*{}` rules apply to the whole page and
 * would silently override any class-based styling here.
 *
 * Owner directive (2026-07-31): magnet landing pages must be on-brand — logo
 * left, CTA right — even on bespoke full-doc magnets CC otherwise owns 100%
 * of. This is a deliberate, narrow amendment to the old "full docs get zero
 * EE chrome" rule — exactly this header, nothing more.
 */

const TSB_DEFAULT_LOGO = 'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/brands/tsb/logo-dark.svg';
const TSB_DEFAULT_HREF = 'https://www.thesuccessfulbookkeeper.com';
const DEFAULT_ACCENT = '#F05A28';

export interface MagnetHeaderBrand {
  name: string;
  logoUrl: string | null;
  logoWhiteUrl: string | null;
  logoLinkUrl: string | null;
  palette: Record<string, unknown> | null;
}

export function paletteAccent(palette: Record<string, unknown> | null | undefined): string {
  if (!palette) return DEFAULT_ACCENT;
  const v = palette.accent ?? palette.primary;
  return typeof v === 'string' && v ? v : DEFAULT_ACCENT;
}

export default function MagnetHeader({
  brand,
  ctaLabel,
  onCta,
}: {
  brand: MagnetHeaderBrand | null;
  /** Omit to render a logo-only header (used on the thank-you screen). */
  ctaLabel?: string;
  onCta?: () => void;
}) {
  // Nav background is dark navy — always prefer the white/on-dark logo
  // variant when one is resolved.
  const logo = brand?.logoWhiteUrl ?? brand?.logoUrl ?? TSB_DEFAULT_LOGO;
  const accent = paletteAccent(brand?.palette);
  return (
    <div
      style={{
        background: '#0A0A2E',
        padding: '0 28px',
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        boxSizing: 'border-box',
      }}
    >
      <a
        href={brand?.logoLinkUrl ?? TSB_DEFAULT_HREF}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
      >
        <img
          src={logo}
          alt={brand?.name ?? 'The Successful Bookkeeper'}
          style={{ height: 36, width: 'auto', display: 'block', border: 0 }}
        />
      </a>
      {ctaLabel && onCta && (
        <button
          type="button"
          onClick={onCta}
          style={{
            background: accent,
            color: '#fff',
            border: 'none',
            padding: '8px 20px',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            flexShrink: 0,
          }}
        >
          {ctaLabel}
        </button>
      )}
    </div>
  );
}
